// Trip-state read + write for web. Ported from mobile/lib/trip-mutations.ts.
//
// The Worker enforces optimistic locking via ETag: GET /state returns an
// `ETag`; PUT /state with `If-Match` succeeds (200, new ETag) or 412 on
// mismatch. This module GETs current state with its ETag, runs a pure
// `(state) => state` updater, PUTs with If-Match, and retries once on 412
// (re-deriving the delta on the partner's latest state — never re-PUTting
// a stale snapshot, which would clobber concurrent mobile edits).
//
// Two web-specific rules the 2026-06-07 spike validated:
//   1. STRIP `stays` before every PUT. The GET response carries `stays`
//      (the Worker re-merges meta.stays), but stays are canonical in meta
//      and edited via PATCH /meta. PUTing them back bakes a stale copy
//      into the state blob (latent drift). putStateWithIfMatch removes it.
//   2. Normalize the `'null'` GET sentinel to EMPTY_STATE — the Worker
//      returns the literal string "null" for a never-written trip, which
//      JSON.parses to null and would throw on `.bookings` access.
//
// Auth is the CF Access cookie (credentials:'include'); the same 401
// interceptor + HTML-route guard as the JSON client in api.ts apply.

import { ApiError, assertJsonRoute, notifyIfUnauthorized } from './api';
import { guestTokenForTrip } from './guest';
import * as core from './trip-state-core';
import * as plan from '@core/plan-core';
import type { BookingPlanFieldsInput, BookingStatus, DayFieldsInput, SlotPatch } from '@core/plan-core';
import type { TripState, Booking, Slot } from './types';

function emptyState(): TripState {
  return { bookings: [], itinerary: {}, tripMeta: {} };
}

function statePath(tripId: string): string {
  return `/api/trips/${encodeURIComponent(tripId)}/state`;
}

interface FetchedState {
  state: TripState;
  etag: string;
}

async function fetchStateWithEtag(tripId: string): Promise<FetchedState> {
  const path = statePath(tripId);
  // Guest share-link sessions read state via the token header; the PUT
  // below intentionally never sends it (guest tokens can't authorize
  // writes server-side, and guest-mode UI hides every write affordance).
  const guestToken = guestTokenForTrip(tripId);
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(guestToken ? { 'X-JG-Guest-Token': guestToken } : {}),
    },
  });
  notifyIfUnauthorized(path, res.status);
  assertJsonRoute(path, res);
  if (!res.ok) {
    throw new ApiError(res.status, null, `GET trip-state failed (HTTP ${res.status})`);
  }
  const etag = res.headers.get('ETag') ?? '"empty"';
  const text = await res.text();
  const state =
    text === 'null' || text === '' ? emptyState() : core.sanitizeTripState(JSON.parse(text) as TripState);
  return { state, etag };
}

async function putStateWithIfMatch(
  tripId: string,
  state: TripState,
  etag: string,
): Promise<FetchedState | null> {
  const path = statePath(tripId);
  // Strip stays before PUT — stays live in meta, edited via PATCH /meta.
  const { stays: _drop, ...body } = state;
  const res = await fetch(path, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'If-Match': etag },
    body: JSON.stringify(body),
  });
  notifyIfUnauthorized(path, res.status);
  if (res.status === 412) return null; // conflict — caller refetches + replays
  assertJsonRoute(path, res);
  if (!res.ok) {
    throw new ApiError(res.status, null, `PUT trip-state failed (HTTP ${res.status})`);
  }
  const newEtag = res.headers.get('ETag') ?? etag;
  const persisted = (await res.json()) as TripState;
  return { state: persisted, etag: newEtag };
}

export type StateUpdater = (state: TripState) => TripState;

// Apply a pure delta to trip state with optimistic locking. Each try
// re-reads the latest state and re-applies the updater, so a conflict with
// another writer (Claude over MCP, the phone) is retried on their result,
// never by re-PUTting a stale copy. Up to five tries with a short, growing,
// jittered wait: the web user test (2026-09-29) lost edits after two
// back-to-back conflicts while MCP was writing the same trip. Returns the
// server-persisted state (render THIS, not local optimistic state).
const MAX_TRIES = 5;
export async function updateTripState(
  tripId: string,
  updater: StateUpdater,
): Promise<TripState> {
  for (let attempt = 0; attempt < MAX_TRIES; attempt++) {
    if (attempt > 0) await new Promise((r) => setTimeout(r, 150 * attempt + Math.random() * 200));
    const { state, etag } = await fetchStateWithEtag(tripId);
    const next = updater(state);
    const result = await putStateWithIfMatch(tripId, next, etag);
    if (result) return result.state;
  }
  throw new ApiError(
    409,
    { error: 'etag_mismatch' },
    'Someone else kept changing this trip while you saved. Your change wasn’t saved; try again.',
  );
}

// Read-only fetch of current trip state (no ETag exposed to callers).
export async function getTripState(tripId: string): Promise<TripState> {
  return (await fetchStateWithEtag(tripId)).state;
}

// ─── Saved items ─────────────────────────────────────────────────────
// Ported verbatim from mobile/lib/trip-mutations.ts. savedItems[category]
// is a string[] of refIds keyed by catalog type (restaurants, hotels, …);
// toggling adds/removes. Keep in lockstep with mobile so saves sync.

export function isSaved(state: TripState, category: string, id: string): boolean {
  const list = state.savedItems?.[category];
  return Array.isArray(list) && list.indexOf(id) >= 0;
}

export function toggleSaved(state: TripState, category: string, id: string): TripState {
  return { ...state, savedItems: core.toggleSaved(state.savedItems, category, id) };
}

// Toggle a saved item and persist via the optimistic-lock write core.
// Returns the server-persisted state so callers can re-derive saved status.
export function toggleSavedItem(
  tripId: string,
  category: string,
  id: string,
): Promise<TripState> {
  return updateTripState(tripId, (s) => toggleSaved(s, category, id));
}

// Per-saved-item freeform note. Keyed `${category}:${refId}` (matches mobile);
// an empty note deletes the key (core.setSavedNote handles the trim/drop).
export function setSavedNoteState(
  state: TripState,
  category: string,
  refId: string,
  note: string,
): TripState {
  return { ...state, savedNotes: core.setSavedNote(state.savedNotes, `${category}:${refId}`, note) };
}

export function setSavedItemNote(
  tripId: string,
  category: string,
  refId: string,
  note: string,
): Promise<TripState> {
  return updateTripState(tripId, (s) => setSavedNoteState(s, category, refId, note));
}

// ─── Bookings + slots (day-detail editing) ───────────────────────────
// Pure transforms mirroring mobile/lib/trip-mutations.ts shapes. Bookings
// live in state.bookings[]; slots in state.itinerary[date].slots[]. id/slotId
// generation matches mobile (base36 timestamp + random). The server validates
// nothing, so these shapes ARE the contract — keep in lockstep with mobile.

export function addBooking(
  state: TripState,
  seed: Partial<Booking> & { kind: string; label: string },
): { state: TripState; id: string } {
  const id = core.genId();
  const entry = { id, createdAt: new Date().toISOString(), ...seed } as Booking;
  return { state: { ...state, bookings: core.appendById(state.bookings, entry) }, id };
}

// A `status` in the patch goes through plan-core's applyBookingPlanFields
// (pending or cleared clears cancelledAt / refunded), never the raw merge;
// a booking that turns pending gives back what it secured
// (releaseReservations).
export function updateBooking(
  state: TripState,
  id: string,
  patch: Partial<Booking> | (Omit<Partial<Booking>, 'status'> & { status?: BookingStatus | null }),
): TripState {
  const { status, ...rest } = patch;
  const next = { ...state, bookings: core.patchById(state.bookings, id, rest as Partial<Booking>) };
  if (status === undefined) return next;
  const now = new Date().toISOString();
  const bookings = (next.bookings ?? []).map((b) => (b.id === id ? plan.applyBookingPlanFields(b, { status }, now) : b));
  const turned = bookings.find((b) => b.id === id);
  const reservations =
    turned && plan.isPendingBooking(turned)
      ? (plan.releaseReservations(next.reservations ?? [], bookings, turned.refId) as TripState['reservations'])
      : next.reservations;
  return { ...next, bookings, reservations };
}

export function removeBooking(state: TripState, id: string): TripState {
  return { ...state, bookings: core.removeById(state.bookings, id) };
}

export function addSlot(
  state: TripState,
  dateISO: string,
  seed: Partial<Slot> & { type: string },
): { state: TripState; slotId: string } {
  const slotId = core.genId();
  // Stamp the catalog type so pins and detail links don't guess it from
  // `type` — the same rule mobile and MCP use (plan-core refKindForSlot).
  const refKind = seed.refKind || plan.refKindForSlot({ slotId, ...seed }) || undefined;
  const slot = { slotId, ...seed, ...(refKind ? { refKind } : {}) } as Slot;
  return { state: { ...state, itinerary: core.appendSlot(state.itinerary, dateISO, slot) }, slotId };
}

// Slot edits go through plan-core's patchSlot: undefined leaves a field
// alone, null / '' clears it (mergeDefined couldn't clear a time or a
// note), and fields the patch doesn't name — role, plan, … — survive.
export function updateSlot(
  state: TripState,
  dateISO: string,
  slotId: string,
  patch: SlotPatch,
): TripState {
  const itinerary = state.itinerary ?? {};
  const next = plan.patchSlot(itinerary, dateISO, slotId, patch);
  return next === itinerary ? state : { ...state, itinerary: next };
}

// Move a slot to another day, keeping its slotId and every field (#574).
export function moveSlot(
  state: TripState,
  fromDate: string,
  toDate: string,
  slotId: string,
  patch?: SlotPatch,
): TripState {
  const itinerary = state.itinerary ?? {};
  const next = plan.moveSlot(itinerary, fromDate, toDate, slotId, patch);
  return next === itinerary ? state : { ...state, itinerary: next };
}

// Turn a slot into a Booking in one write (#567): plan-core's bookSlot
// (keeps the place link, secures a tracking reservation, snapshots the
// slot in fromSlot), then the form's booking fields on top. A slot the
// one-step rule refuses (a freetext note) still converts when the user
// fills the booking form explicitly.
export function convertSlotToBooking(
  state: TripState,
  dateISO: string,
  slotId: string,
  fields: Partial<Booking> & { kind: string },
): TripState {
  const bookingId = core.genId();
  const now = new Date().toISOString();
  const current = {
    itinerary: state.itinerary ?? {},
    bookings: state.bookings ?? [],
    reservations: state.reservations ?? [],
  };
  let next = plan.bookSlot(current, {
    dateISO,
    slotId,
    bookingId,
    now,
    kind: fields.kind,
    time: fields.time ?? null,
    // A requested booking doesn't secure the reservation tracker (#605).
    ...(fields.status === 'pending' ? { status: 'pending' as const } : {}),
  });
  if (next === current) {
    const slot = (current.itinerary[dateISO]?.slots ?? []).find((x) => x.slotId === slotId);
    if (!slot) return state;
    next = {
      ...current,
      itinerary: core.removeSlot(current.itinerary, dateISO, slotId),
      bookings: [
        ...current.bookings,
        { id: bookingId, createdAt: now, fromSlot: { ...slot, dateISO } } as unknown as Booking,
      ],
    };
  }
  const merged: TripState = {
    ...state,
    itinerary: next.itinerary,
    bookings: next.bookings as Booking[],
    reservations: next.reservations as TripState['reservations'],
  };
  return updateBooking(merged, bookingId, fields);
}

export function removeSlot(state: TripState, dateISO: string, slotId: string): TripState {
  return { ...state, itinerary: core.removeSlot(state.itinerary, dateISO, slotId) };
}

// Async enqueue helpers — each wraps the pure transform in the optimistic-lock
// write core (If-Match + 412 retry). Return the server-persisted state.
export function enqueueAddBooking(
  tripId: string,
  seed: Partial<Booking> & { kind: string; label: string },
): Promise<TripState> {
  return updateTripState(tripId, (s) => addBooking(s, seed).state);
}
export function enqueueUpdateBooking(
  tripId: string,
  id: string,
  patch: Partial<Booking>,
): Promise<TripState> {
  return updateTripState(tripId, (s) => updateBooking(s, id, patch));
}
export function enqueueRemoveBooking(tripId: string, id: string): Promise<TripState> {
  return updateTripState(tripId, (s) => removeBooking(s, id));
}
export function enqueueAddSlot(
  tripId: string,
  dateISO: string,
  seed: Partial<Slot> & { type: string },
): Promise<TripState> {
  return updateTripState(tripId, (s) => addSlot(s, dateISO, seed).state);
}
export function enqueueUpdateSlot(
  tripId: string,
  dateISO: string,
  slotId: string,
  patch: SlotPatch,
): Promise<TripState> {
  return updateTripState(tripId, (s) => updateSlot(s, dateISO, slotId, patch));
}
// Day title / plan variants / the variant the day is following (#567),
// with plan-core's applyDayFields (an activePlan must name a variant).
export function enqueueUpdateDay(
  tripId: string,
  dateISO: string,
  fields: DayFieldsInput,
): Promise<TripState> {
  return updateTripState(tripId, (s) => {
    const itinerary = s.itinerary ?? {};
    const day = itinerary[dateISO] ?? { slots: [] };
    return { ...s, itinerary: { ...itinerary, [dateISO]: plan.applyDayFields(day, fields) } };
  });
}

// Cancel / reinstate / refund a booking (#567) with plan-core's rules:
// cancelling stamps cancelledAt, clearing status clears cancelledAt and
// refunded, and refunded only sticks on a cancelled booking.
export function enqueueUpdateBookingPlan(
  tripId: string,
  id: string,
  fields: BookingPlanFieldsInput,
): Promise<TripState> {
  return updateTripState(tripId, (s) => {
    const now = new Date().toISOString();
    const bookings = (s.bookings ?? []).map((b) =>
      b.id === id ? plan.applyBookingPlanFields(b, fields, now) : b,
    );
    return { ...s, bookings };
  });
}
export function enqueueMoveSlot(
  tripId: string,
  fromDate: string,
  toDate: string,
  slotId: string,
  patch?: SlotPatch,
): Promise<TripState> {
  return updateTripState(tripId, (s) => moveSlot(s, fromDate, toDate, slotId, patch));
}
export function enqueueConvertSlotToBooking(
  tripId: string,
  dateISO: string,
  slotId: string,
  fields: Partial<Booking> & { kind: string },
): Promise<TripState> {
  return updateTripState(tripId, (s) => convertSlotToBooking(s, dateISO, slotId, fields));
}
// Booking → slot in one write (the reverse of Mark booked): plan-core's
// unbookSlot restores the slot from the booking's fromSlot snapshot
// (original id, type and plan fields) — or builds one from the booking —
// with the form's label / time / notes / link on top, and returns a
// secured reservation for the place to tracking.
export function enqueueConvertBookingToSlot(
  tripId: string,
  bookingId: string,
  dateISO: string,
  seed: Partial<Slot> & { type: string },
): Promise<TripState> {
  return updateTripState(tripId, (s) => {
    const current = {
      itinerary: s.itinerary ?? {},
      bookings: s.bookings ?? [],
      reservations: s.reservations ?? [],
    };
    const next = plan.unbookSlot(current, {
      bookingId,
      slotId: core.genId(),
      dateISO,
      patch: {
        label: seed.label ?? null,
        time: seed.time ?? null,
        notes: seed.notes ?? null,
        ...(seed.phone !== undefined ? { phone: seed.phone ?? null } : {}),
        ...(seed.refId !== undefined ? { refId: seed.refId ?? null } : {}),
        ...(seed.refKind !== undefined ? { refKind: seed.refKind ?? null } : {}),
      },
    });
    if (next === current) return s;
    // The editor's plan flags (to book, must / option, band, decide by)
    // go on the slot the booking became.
    const flags: SlotPatch = {};
    for (const k of ['must', 'role', 'toBook', 'plan', 'band', 'lane', 'decideBy'] as const) {
      if (seed[k] !== undefined) (flags as Record<string, unknown>)[k] = seed[k];
    }
    let itinerary = next.itinerary;
    if (Object.keys(flags).length) {
      const before = new Set((current.itinerary[dateISO]?.slots ?? []).map((x) => x.slotId));
      const made = (itinerary[dateISO]?.slots ?? []).find((x) => !before.has(x.slotId));
      if (made) itinerary = plan.patchSlot(itinerary, dateISO, made.slotId, flags);
    }
    return {
      ...s,
      itinerary,
      bookings: next.bookings as Booking[],
      reservations: next.reservations as TripState['reservations'],
    };
  });
}
export function enqueueRemoveSlot(
  tripId: string,
  dateISO: string,
  slotId: string,
): Promise<TripState> {
  return updateTripState(tripId, (s) => removeSlot(s, dateISO, slotId));
}

// The day's free-text note (tripMeta.dayNotes[date], the note the web
// and MCP set_day_note write; mobile's dated notes are
// tripMeta.dayExtraNotes). An empty note clears it.
export function enqueueSetDayNote(tripId: string, dateISO: string, text: string): Promise<TripState> {
  return updateTripState(tripId, (s) => {
    const tm = { ...(s.tripMeta ?? {}) } as Record<string, unknown>;
    const notes = { ...((tm.dayNotes as Record<string, string> | undefined) ?? {}) };
    const t = text.trim();
    if (t) notes[dateISO] = t;
    else delete notes[dateISO];
    tm.dayNotes = notes;
    return { ...s, tripMeta: tm };
  });
}

// Delete a day's plan variant with plan-core's rules: its items rejoin the
// base plan, and the day goes back to the base plan if it followed it.
export function enqueueRemoveDayPlan(tripId: string, dateISO: string, planId: string): Promise<TripState> {
  return updateTripState(tripId, (s) => plan.removeDayPlanInState(s as any, dateISO, planId) as TripState);
}
